import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
} from 'chart.js';
import {
    ChartSpline,
    Search,
    Building2,
    CheckCircle,
    AlertTriangle,
    Layers,
    ArrowLeft,
    TrendingUp,
} from "lucide-react";
import Skeleton from "react-loading-skeleton";
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import axiosInstance from "../../services/api/axios";
// v2 (F9/§B): building-status read via service wrapper
import { getConstructionEvents } from "../../services/wbs/index";
import { useUser } from "../../context/UserContext";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
);

/* ---------- formatters ---------- */
const fmtInt = (n) => Number(n || 0).toLocaleString("en-IN");
const fmtPct = (n) => (Math.trunc(Number(n || 0) * 100) / 100).toFixed(2);
const fmtSqft = (n) => {
    const v = Number(n || 0);
    if (v >= 1e5) return `${(v / 1e5).toFixed(2)}L sqft`;
    if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K sqft`;
    return `${Math.round(v)} sqft`;
};

const KpiTile = ({ icon: Icon, label, value, subtitle, accent = "slate" }) => {
    const accents = {
        flamingo: "bg-flamingo-50 text-flamingo-600",
        slate: "bg-slate-100 text-slate-700",
        emerald: "bg-emerald-50 text-emerald-600",
        indigo: "bg-indigo-50 text-indigo-600",
        amber: "bg-amber-50 text-amber-700",
    };
    return (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex flex-col gap-3 hover:shadow-md transition-shadow">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${accents[accent]}`}>
                <Icon size={20} />
            </div>
            <div>
                <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">{label}</p>
                <p className="text-2xl font-bold text-slate-900 mt-1 tabular-nums">{value}</p>
                {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
            </div>
        </div>
    );
};

const TileSkeleton = () => (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 h-32">
        <Skeleton circle height={40} width={40} />
        <Skeleton height={12} width="70%" className="mt-3" />
        <Skeleton height={28} width="50%" className="mt-2" />
    </div>
);

// Function to generate an array of months
function getSurroundingMonths(date) {
    const currentDate = new Date(date); // Create a new Date object
    const months = [];

    // Add the previous three months
    for (let i = -3; i < 0; i++) {
        const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + i, 1);
        months.push(newDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }));
    }

    // Add the current month
    months.push(currentDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }));

    // Add the next three months
    for (let i = 1; i <= 3; i++) {
        const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + i, 1);
        months.push(newDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }));
    }

    return months;
}


function CompletionReport() {
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [projects, setProjects] = useState({}); // Store project-wise data
    const [initialLoad, setInitialLoad] = useState(true); // Track initial data load
    const [searchTerm, setSearchTerm] = useState(''); // State for search term
    const [selectedPhase, setSelectedPhase] = useState('all'); // State for phase filter
    const [selectedProjectId, setSelectedProjectId] = useState(''); // "" = all projects
    const [projectOptions, setProjectOptions] = useState([]);
    const [summary, setSummary] = useState(null);
    const { userInfo } = useUser(); // Get user info to access customer ID

    // Project picker (shared with dashboard)
    useEffect(() => {
        let mounted = true;
        axiosInstance.get("/dashboard/projects")
            .then((res) => {
                if (mounted) setProjectOptions(Array.isArray(res) ? res : res?.items || []);
            })
            .catch(() => { if (mounted) setProjectOptions([]); });
        return () => { mounted = false; };
    }, []);

    // Summary KPIs (RBAC-scoped, project-filtered)
    useEffect(() => {
        let mounted = true;
        const params = {};
        if (selectedProjectId) params.projectId = selectedProjectId;
        axiosInstance.get("/reports/progress-summary", { params })
            .then((res) => { if (mounted) setSummary(res); })
            .catch(() => { if (mounted) setSummary(null); });
        return () => { mounted = false; };
    }, [selectedProjectId]);

    // Use useCallback to memoize the data processing function
    const processData = useCallback((rawData) => {
        const groupedProjects = {};
        rawData.forEach((item) => {
            const building = item?.building;
            const linkedPlan = building?.constructionLinkedPlan;
            const projectPhase = linkedPlan?.projectPhase;
            const project = projectPhase?.project;

            if (!building || !linkedPlan || !projectPhase || !project) {
                return;
            }

            const projectId = project.projectId;
            const projectPhaseId = projectPhase.projectPhaseId;
            const projectPhaseName = projectPhase.name;
            
            if (!groupedProjects[projectId]) {
                groupedProjects[projectId] = {
                    projectName: project.projectName,
                    data: [],
                    buildings: [], // List of buildings in the project
                    phases: [], // List of phases in the project
                };
            }
            groupedProjects[projectId].data.push(item);

            // Add the building to the project's building list (if it's not already there)
            if (!groupedProjects[projectId].buildings.find(b => b.buildingId === item.building.buildingId)) {
                groupedProjects[projectId].buildings.push({
                    buildingId: item.building.buildingId,
                    buildingName: item.building.buildingName,
                    projectPhaseId: projectPhaseId,
                    projectPhaseName: projectPhaseName,
                });
            }

            // Add the phase to the project's phase list (if it's not already there)
            if (!groupedProjects[projectId].phases.find(p => p.projectPhaseId === projectPhaseId)) {
                groupedProjects[projectId].phases.push({
                    projectPhaseId: projectPhaseId,
                    projectPhaseName: projectPhaseName,
                });
            }
        });
        return groupedProjects;
    }, []);

    useEffect(() => {
        const fetchProjectsAndCompletion = async () => {
            setLoading(true);
            try {
                // Get customer ID from user info
                const customerId = userInfo?.loginIdentifierDetails?.customer?.customerId;
                
                if (!customerId) {
                    console.warn("Customer ID not found in userInfo. Cannot fetch projects.");
                    setError(new Error("Customer ID not found"));
                    setLoading(false);
                    setInitialLoad(false);
                    return;
                }

                // First, fetch projects for this customer
                const projectsResponse = await axiosInstance.get('/projects', {
                    params: {
                        customerId: customerId,
                    },
                });

                // Handle both paginated and non-paginated responses
                const projectsArray = projectsResponse?.items || (Array.isArray(projectsResponse) ? projectsResponse : []);

                if (!projectsArray || projectsArray.length === 0) {
                    console.warn("No projects found for customer ID:", customerId);
                    setData([]);
                    setLoading(false);
                    setInitialLoad(false);
                    return;
                }

                // Scope to selectedProjectId when the picker is set; otherwise pull all visible projects.
                const projectIds = selectedProjectId
                    ? [Number(selectedProjectId)]
                    : projectsArray.map(project => project.projectId);

                // v2 (F9/§B): use getConstructionEvents service wrapper (same endpoint, v2 shape)
                const completionPromises = projectIds.map(projectId =>
                    getConstructionEvents({
                        projectId: projectId,
                        // Add projectPhaseId if a specific phase is selected
                        ...(selectedPhase !== 'all' && { projectPhaseId: parseInt(selectedPhase) })
                    }).catch(error => {
                        console.warn(`Error fetching completion data for project ${projectId}:`, error);
                        return []; // Return empty array for failed requests
                    })
                );

                const completionResults = await Promise.all(completionPromises);

                // Flatten all completion data and support paginated responses
                const allCompletionData = completionResults
                    .flatMap((result) => {
                        if (Array.isArray(result)) return result;
                        if (Array.isArray(result?.items)) return result.items;
                        return [];
                    })
                    .filter((item) => item && item?.building?.constructionLinkedPlan?.projectPhase?.project);
                
                setData(allCompletionData);
                setLoading(false);
                setInitialLoad(false);
            } catch (error) {
                console.error("Error fetching projects and completion data:", error);
                setError(error);
                setLoading(false);
                setInitialLoad(false);
            }
        };

        fetchProjectsAndCompletion();
    }, [userInfo?.loginIdentifierDetails?.customer?.customerId, selectedPhase, selectedProjectId]);

    useEffect(() => {
        // Process data when it's available
        if (data && data.length > 0) {
            setProjects(processData(data));
        }
    }, [data, processData]);

    // Function to handle search input change
    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
    };

    // Function to handle phase filter change
    const handlePhaseChange = (event) => {
        setSelectedPhase(event.target.value);
    };

    // Filter projects based on search term and phase
    const filteredProjects = Object.entries(projects).filter(([projectId, projectData]) => {
        const matchesSearch = projectData.projectName.toLowerCase().includes(searchTerm.toLowerCase());
        
        if (selectedPhase === 'all') {
            return matchesSearch;
        }
        
        // Filter by phase - check if any building in this project belongs to the selected phase
        const hasPhaseData = projectData.buildings.some(building => 
            building.projectPhaseId.toString() === selectedPhase
        );
        
        return matchesSearch && hasPhaseData;
    });

    const selectedProjectLabel =
        selectedProjectId === ""
            ? "All projects"
            : projectOptions.find((p) => String(p.projectId) === String(selectedProjectId))
                ?.projectName || "Project";

    const sortedSummaryProjects = useMemo(
        () => [...(summary?.projects || [])].sort((a, b) => b.progressPercent - a.progressPercent),
        [summary]
    );

    return (
        <div className="p-4 sm:p-6 lg:p-8 flex flex-col gap-6 min-h-[calc(100vh-4rem)] bg-slate-50/40">
            {/* Hero band */}
            <div className="rounded-2xl shadow-sm bg-gradient-to-r from-slate-800 via-slate-700 to-slate-600 p-5 lg:p-6 text-white">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                    <div>
                        <p className="text-xs uppercase tracking-wider text-slate-300 flex items-center gap-1.5">
                            <ChartSpline size={12} /> Reports
                        </p>
                        <h1 className="text-xl sm:text-2xl font-semibold tracking-tight mt-1">
                            Construction progress
                        </h1>
                        <p className="text-xs text-slate-300 mt-1">
                            {selectedProjectLabel}
                            {selectedPhase !== "all" && (
                                <>
                                    <span className="mx-1.5 text-slate-500">•</span>
                                    Filtered by phase
                                </>
                            )}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() => navigate(-1)}
                        className="inline-flex items-center gap-1 text-xs font-medium text-slate-200 hover:text-white self-start lg:self-end"
                    >
                        <ArrowLeft size={14} /> Back
                    </button>
                </div>

                <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="flex flex-col gap-1.5">
                        <label className="text-[11px] uppercase tracking-wider text-slate-300">Project</label>
                        <select
                            value={selectedProjectId}
                            onChange={(e) => setSelectedProjectId(e.target.value)}
                            className="px-3 py-2 rounded-lg bg-white/10 text-white text-sm border border-white/20 focus:bg-white/20 focus:border-white/40 focus:outline-none"
                        >
                            <option value="" className="text-slate-900">All projects</option>
                            {projectOptions.map((p) => (
                                <option key={p.projectId} value={p.projectId} className="text-slate-900">
                                    {p.projectName}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <label className="text-[11px] uppercase tracking-wider text-slate-300">Phase</label>
                        <select
                            value={selectedPhase}
                            onChange={(e) => setSelectedPhase(e.target.value)}
                            className="px-3 py-2 rounded-lg bg-white/10 text-white text-sm border border-white/20 focus:bg-white/20 focus:border-white/40 focus:outline-none"
                        >
                            <option value="all" className="text-slate-900">All phases</option>
                            {Object.values(projects).flatMap(p => p.phases).map((ph) => (
                                <option
                                    key={ph.projectPhaseId}
                                    value={ph.projectPhaseId.toString()}
                                    className="text-slate-900"
                                >
                                    {ph.projectPhaseName}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <label className="text-[11px] uppercase tracking-wider text-slate-300">Search</label>
                        <div className="relative">
                            <Search
                                size={14}
                                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-300 pointer-events-none"
                            />
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={handleSearchChange}
                                placeholder="Search by project name…"
                                className="px-3 py-2 pl-8 rounded-lg bg-white/10 text-white text-sm placeholder-slate-400 border border-white/20 focus:bg-white/20 focus:border-white/40 focus:outline-none w-full"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {error && (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 text-rose-700 px-4 py-3 text-sm">
                    {error.message || String(error)}
                </div>
            )}

            {/* KPI tiles */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {summary === null && loading ? (
                    <>
                        <TileSkeleton />
                        <TileSkeleton />
                        <TileSkeleton />
                        <TileSkeleton />
                    </>
                ) : (
                    <>
                        <KpiTile
                            icon={Layers}
                            label="Projects tracked"
                            value={fmtInt(summary?.totalProjects)}
                            subtitle="Visible to you"
                            accent="slate"
                        />
                        <KpiTile
                            icon={TrendingUp}
                            label="Avg progress"
                            value={`${fmtPct(summary?.averageProgressPercent)}%`}
                            subtitle="Mean across portfolio"
                            accent="flamingo"
                        />
                        <KpiTile
                            icon={CheckCircle}
                            label="On track"
                            value={fmtInt(summary?.onTrackCount)}
                            subtitle="≥ 50% construction"
                            accent="emerald"
                        />
                        <KpiTile
                            icon={AlertTriangle}
                            label="Behind"
                            value={fmtInt(summary?.behindCount)}
                            subtitle="< 50% construction"
                            accent="amber"
                        />
                    </>
                )}
            </div>

            {/* Project progress ranking — overview before the detailed line charts */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
                <div className="px-5 py-4 border-b border-slate-200">
                    <h3 className="text-base font-semibold text-slate-900">Project progress</h3>
                    <p className="text-xs text-slate-500">
                        Sorted by construction progress · {fmtInt(summary?.totalMilestones)} milestones logged
                    </p>
                </div>
                <div className="divide-y divide-slate-100">
                    {summary === null ? (
                        <div className="p-5">
                            <Skeleton height={48} className="mb-2" />
                            <Skeleton height={48} className="mb-2" />
                            <Skeleton height={48} />
                        </div>
                    ) : sortedSummaryProjects.length === 0 ? (
                        <div className="p-8 text-center text-sm text-slate-500">
                            No project progress data yet.
                        </div>
                    ) : (
                        sortedSummaryProjects.map((p) => {
                            const sqftShare =
                                p.sqftInventory > 0 ? (p.sqftCompleted / p.sqftInventory) * 100 : 0;
                            return (
                                <div
                                    key={p.projectId}
                                    className="px-5 py-4 hover:bg-slate-50/60 transition-colors cursor-pointer flex items-center gap-4"
                                    onClick={() => navigate(`/project-details/${p.projectId}`)}
                                >
                                    <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
                                        <Building2 size={18} className="text-slate-600" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-baseline justify-between gap-3">
                                            <p className="text-sm font-semibold text-slate-900 truncate">
                                                {p.projectName}
                                            </p>
                                            <p className="text-xs text-slate-500 tabular-nums">
                                                {fmtInt(p.milestonesLogged)} milestones
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-3 mt-1.5">
                                            <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                                                <div
                                                    className={`h-full bg-gradient-to-r ${
                                                        p.progressPercent >= 50
                                                            ? "from-emerald-400 to-emerald-600"
                                                            : "from-amber-400 to-amber-600"
                                                    }`}
                                                    style={{ width: `${Math.min(100, p.progressPercent)}%` }}
                                                />
                                            </div>
                                            <span className="text-xs text-slate-600 tabular-nums shrink-0">
                                                {fmtPct(p.progressPercent)}%
                                            </span>
                                        </div>
                                        <p className="text-[11px] text-slate-500 mt-1">
                                            {fmtSqft(p.sqftCompleted)} of {fmtSqft(p.sqftInventory)} ·{" "}
                                            {fmtPct(sqftShare)}% sqft delivered
                                        </p>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>

            {/* Detailed monthly timeline charts per project */}
            {loading && initialLoad ? (
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
                    <Skeleton height={28} width="40%" />
                    <Skeleton height={320} className="mt-3" />
                </div>
            ) : (
                <div className="flex flex-col gap-4">
                    {filteredProjects.map(([projectId, projectData]) =>
                        projectData.data.length > 0 ? (
                            <div
                                key={projectId}
                                className="bg-white rounded-2xl border border-slate-200 shadow-sm"
                            >
                                <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between gap-3">
                                    <div>
                                        <h3 className="text-base font-semibold text-slate-900">
                                            {projectData.projectName}
                                        </h3>
                                        <p className="text-xs text-slate-500">
                                            Construction progress timeline · {projectData.buildings.length} building
                                            {projectData.buildings.length === 1 ? "" : "s"}
                                        </p>
                                    </div>
                                </div>
                                <div className="p-4 sm:p-5">
                                    <ProjectCompletionChart
                                        projectId={projectId}
                                        projectData={projectData.data}
                                        projectName={projectData.projectName}
                                        buildings={projectData.buildings}
                                        selectedPhase={selectedPhase}
                                    />
                                </div>
                            </div>
                        ) : null
                    )}
                    {data.length === 0 && !loading && (
                        <div className="bg-white rounded-2xl border border-dashed border-slate-300 px-6 py-16 text-center">
                            <Building2 size={32} className="mx-auto text-slate-300" />
                            <p className="mt-3 text-sm font-semibold text-slate-700">
                                No construction milestones yet
                            </p>
                            <p className="text-xs text-slate-500 mt-1">
                                Log progress on a building to start tracking it here.
                            </p>
                        </div>
                    )}
                    {filteredProjects.length === 0 && searchTerm !== "" && (
                        <div className="bg-white rounded-2xl border border-dashed border-slate-300 px-6 py-12 text-center text-sm text-slate-500">
                            No projects match "{searchTerm}".
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}


function ProjectCompletionChart({ projectId, projectData, projectName, buildings, selectedPhase }) {
    const [selectedBuilding, setSelectedBuilding] = useState('all'); // Initialize with 'all'
    const [chartData, setChartData] = useState(null);
    const [referenceDate, setReferenceDate] = useState(new Date());
    const chartRef = useRef(null);

    const handleChange = (event) => {
        setSelectedBuilding(event.target.value);
    };

    const calculateChartData = useCallback((filteredData, newReferenceDate) => {
        const allMonths = getSurroundingMonths(newReferenceDate);
        const monthlyCompletion = {}; // Structure: { [month]: { [buildingId]: [items] } }

        // Filter data by selected phase if not 'all'
        const phaseFilteredData = selectedPhase === 'all' 
            ? filteredData 
            : filteredData.filter(item => 
                item?.building?.constructionLinkedPlan?.projectPhase?.projectPhaseId?.toString() === selectedPhase
            );

        phaseFilteredData.forEach(item => {
            const monthYear = new Date(item.achievedDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
            const buildingId = item?.building?.buildingId;
            if (!buildingId) return;

            if (!monthlyCompletion[monthYear]) {
                monthlyCompletion[monthYear] = {};
            }

            if (!monthlyCompletion[monthYear][buildingId]) {
                monthlyCompletion[monthYear][buildingId] = 0;
            }

            monthlyCompletion[monthYear][buildingId] += item.percentAchieved; // Sum the percentages
        });

        const datasets = [];

        if (selectedBuilding === 'all') {
            // Calculate average completion across all buildings (filtered by phase)
            const avgCompletion = {};
            const phaseFilteredBuildings = selectedPhase === 'all' 
                ? buildings 
                : buildings.filter(building => building.projectPhaseId.toString() === selectedPhase);
                
            allMonths.forEach(month => {
                let totalCompletion = 0;
                let buildingCount = 0;
                phaseFilteredBuildings.forEach(building => {
                    if (monthlyCompletion[month] && monthlyCompletion[month][building.buildingId]) {
                        totalCompletion += monthlyCompletion[month][building.buildingId];
                        buildingCount++;
                    }
                });
                avgCompletion[month] = buildingCount > 0 ? totalCompletion / buildingCount : 0;
            });

            datasets.push({
                label: '📊 Project Completion (Average Across All Buildings)',
                data: allMonths.map(month => avgCompletion[month]),
                borderColor: getChartColor(0),
                backgroundColor: getChartColor(0) + '20',
                fill: true,
                tension: 0.4,
                pointBackgroundColor: getChartColor(0),
                pointBorderColor: '#ffffff',
                pointBorderWidth: 2,
                pointRadius: 6,
                pointHoverRadius: 8,
                pointHoverBackgroundColor: getChartColor(0),
                pointHoverBorderColor: '#ffffff',
                pointHoverBorderWidth: 3,
            });
        } else {
            // Show completion for the selected building
            const building = buildings.find(b => b.buildingId === selectedBuilding);
            if (building) {
                const buildingName = building.buildingName;
                const buildingId = building.buildingId;
                const color = getChartColor(1);

                datasets.push({
                    label: `🏢 ${buildingName}`,
                    data: allMonths.map(month => {
                        return monthlyCompletion[month] && monthlyCompletion[month][selectedBuilding] ? monthlyCompletion[month][selectedBuilding] : 0;
                    }),
                    borderColor: color,
                    backgroundColor: color + '20',
                    fill: true,
                    tension: 0.4,
                    buildingId: buildingId,
                    pointBackgroundColor: color,
                    pointBorderColor: '#ffffff',
                    pointBorderWidth: 2,
                    pointRadius: 6,
                    pointHoverRadius: 8,
                    pointHoverBackgroundColor: color,
                    pointHoverBorderColor: '#ffffff',
                    pointHoverBorderWidth: 3,
                });
            }
        }

        const chartData = {
            labels: allMonths,
            datasets: datasets,
        };
        return chartData;
    }, [selectedBuilding, buildings, selectedPhase]);



    useEffect(() => {
        const filteredData = projectData; // No filtering needed based on buildingId
        const latestDate = filteredData.reduce((max, item) => max === null || new Date(item.achievedDate) > max ? new Date(item.achievedDate) : max, null);
        const newReferenceDate = latestDate ? latestDate : new Date();
        setReferenceDate(newReferenceDate);

        setChartData(calculateChartData(filteredData, newReferenceDate));
    }, [projectData, calculateChartData]);


    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            intersect: false,
            mode: 'index',
        },
        scales: {
            x: {
                title: { 
                    display: true, 
                    text: 'Timeline (Months)', 
                    font: { size: 14, weight: 'bold' },
                    color: '#374151'
                },
                grid: {
                    color: '#f3f4f6',
                    drawBorder: false,
                },
                ticks: {
                    color: '#6b7280',
                    font: { size: 12 }
                }
            },
            y: {
                title: { 
                    display: true, 
                    text: 'Completion Percentage (%)', 
                    font: { size: 14, weight: 'bold' },
                    color: '#374151'
                },
                min: 0,
                max: 100,
                grid: {
                    color: '#f3f4f6',
                    drawBorder: false,
                },
                ticks: {
                    stepSize: 10,
                    color: '#6b7280',
                    font: { size: 12 },
                    callback: function (value) {
                        return value + '%';
                    }
                }
            },
        },
        plugins: {
            tooltip: {
                backgroundColor: 'rgba(17, 24, 39, 0.95)',
                titleColor: '#f9fafb',
                bodyColor: '#e5e7eb',
                borderColor: '#374151',
                borderWidth: 1,
                cornerRadius: 8,
                bodyFont: { size: 13 },
                titleFont: { size: 14, weight: 'bold' },
                padding: 12,
                callbacks: {
                    label: (context) => {
                        const month = context.label;
                        const dataset = context.dataset;
                        const buildingId = dataset.buildingId;

                        if (selectedBuilding === 'all') {
                            return [`📅 ${month}`, `📊 Project Completion: ${context.parsed.y.toFixed(2)}%`];
                        }

                        let filteredDataForMonth = projectData.filter(item => {
                            const itemMonthYear = new Date(item.achievedDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
                            const matchesMonth = itemMonthYear === month;
                            const matchesBuilding = item?.building?.buildingId === buildingId;
                            const matchesPhase = selectedPhase === 'all' || 
                                item?.building?.constructionLinkedPlan?.projectPhase?.projectPhaseId?.toString() === selectedPhase;
                            return matchesMonth && matchesBuilding && matchesPhase;
                        });

                        let tooltipLines = [];

                        if (filteredDataForMonth && filteredDataForMonth.length > 0) {
                            tooltipLines.push(`📅 ${month}`);
                            let totalPercentage = 0;
                            filteredDataForMonth.forEach(item => {
                                tooltipLines.push(`  • ${item.statusType.typeName}: ${item.percentAchieved}%`);
                                totalPercentage += parseFloat(item.percentAchieved);
                            });
                            tooltipLines.push(`📈 Total: ${totalPercentage.toFixed(1)}%`);
                        } else {
                            tooltipLines.push(`📅 ${month} - No data available`);
                        }

                        return tooltipLines;
                    },
                },
            },
            legend: {
                display: true,
                position: 'top',
                labels: {
                    usePointStyle: true,
                    pointStyle: 'circle',
                    font: { size: 13, weight: '600' },
                    color: '#374151',
                    padding: 20,
                    generateLabels: function(chart) {
                        const original = ChartJS.defaults.plugins.legend.labels.generateLabels;
                        const labels = original.call(this, chart);
                        labels.forEach(label => {
                            label.fontColor = '#374151';
                        });
                        return labels;
                    }
                }
            },
        },
    };
    // Function to generate beautiful colors for lines
    const getChartColor = (index) => {
        const colors = [
            '#3b82f6', // Blue
            '#10b981', // Emerald
            '#f59e0b', // Amber
            '#ef4444', // Red
            '#8b5cf6', // Violet
            '#06b6d4', // Cyan
            '#84cc16', // Lime
            '#f97316', // Orange
            '#ec4899', // Pink
            '#6366f1', // Indigo
        ];
        return colors[index % colors.length];
    };

    return (
        <div className="flex flex-col">
            <div className="flex justify-end mb-2">
                <Select
                    value={selectedBuilding}
                    onChange={handleChange}
                    displayEmpty
                    //inputProps={{ 'aria-label': 'Without label' }}
                    className="w-48 h-10 mr-6 text-sm"
                >
                    <MenuItem value="all">Select a Building</MenuItem>
                    {buildings
                        .filter(building => 
                            selectedPhase === 'all' || building.projectPhaseId.toString() === selectedPhase
                        )
                        .map(building => (
                            <MenuItem key={building.buildingId} value={building.buildingId}>
                                {building.buildingName} ({building.projectPhaseName})
                            </MenuItem>
                        ))}
                </Select>
            </div>
            <div className="h-96 w-full">
                {chartData && chartData.datasets.length > 0 ? (  // Conditional rendering here
                    <Line data={chartData} options={chartOptions} ref={chartRef} />
                ) : (
                    <div>No data available.</div>
                )}
            </div>
        </div>
    );
}

export default CompletionReport;